import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import InputAdornment from '@mui/material/InputAdornment';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { formatCurrency, parseDecimal, currencySymbolFor } from '../common/common';
import ContractPrice from '../domain/contractprice';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteButton from '../common/deletebutton';
import { round } from 'mathjs';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

const styles = {
    withBottomBorder: {
        borderBottom: '1px solid lightgray',
        pb: '0.5em',
    },
    withExtraPadding: {
        pt: '2em',
        pb: '2em',
    },
    withTopMargin: {
        mt: '10px',
    },
    rowDisabled: {
        color: 'lightgray',
    },
    highlight: {
        fontWeight: 'bold',
    },
    button: {
        p: '0.5em',
    },
    productMarginWarning: {
        color: 'darkred',
    },
    productMarginOk: {
        color: 'green',
    },
    sectionDesktop: {
        display: { xs: 'none', md: 'flex' },
    },
    sectionMobile: {
        display: { xs: 'flex', md: 'none' },
    },
    bundleProductDetails: {
        fontSize: '0.75em',
    },
    swapIconCol: {
        textAlign: 'center',
    },
};

export default function ContractPricesProduct(props) {
    const row = props.priceRow;
    const edit = props.edit;
    const pricingRule = props.pricingRule;
    const highlight = props.highlight;
    const pricingRequest = props.pricingRequest;
    const [saving, setSaving] = useState(false);
    const { t } = useTranslation();

    const getStandardClassLabel = (standardClass) => {
        switch (standardClass) {
            case 0:
                return t('product.nonStandard');
            case 1:
                return t('product.standardClass') + ' 1';
            case 2:
                return t('product.standardClass') + ' 2';
            default:
                return '';
        }
    };

    const getPricingRuleTooltip = () => {
        if (!pricingRule) return '';
        const materials = pricingRule.materials || [];
        return `${pricingRule.type} ${getStandardClassLabel(pricingRule.standardClass)} -${pricingRule.discount}% ${materials.join(', ')}`;
    };

    useEffect(() => {}, []);

    const productRowSx = {
        ...styles.withBottomBorder,
        ...(edit ? styles.withExtraPadding : {}),
        ...(edit || highlight ? styles.highlight : {}),
        ...(pricingRule || props.disabled ? styles.rowDisabled : {}),
    };

    const onSelectRow = () => {
        if (pricingRule || props.disabled) return;
        props.selectRow();
    };

    const editRow = () => {
        props.editRow();
    };

    const handleDiscountChange = (e) => {
        let value = e.target.value.toString().replace(',', '.');
        if (!/^-?\d*[.]?\d{0,4}$/.test(value)) {
            return;
        } else if (!(value > 100)) {
            const newCp = new ContractPrice(row);
            newCp.discount = value; //round(value, 3);
            props.onChange(newCp);
        }
    };

    const sanitizeDiscountValue = () => {
        const newCp = new ContractPrice(row);
        newCp.discount = round(parseDecimal(row.discount), 3);
        props.onChange(newCp);
    };

    const handlePriceChange = (e) => {
        const value = e.target.value.toString().replace(',', '.');
        const newCp = new ContractPrice(row);
        if (!/^-?\d*[.]?\d{0,6}$/.test(value)) {
            return;
        } else {
            newCp.price = value; //round(value, 3);
            props.onChange(newCp);
        }
    };

    const sanitizePriceValue = () => {
        const newCp = new ContractPrice(row);
        if (newCp.price === '') {
            newCp.price = null;
        } else {
            newCp.price = round(newCp.price, 3);
        }
        props.onChange(newCp);
    };

    const onDiscountPriceSwap = () => {
        const newCp = new ContractPrice(row);
        if (newCp.discount === null) {
            newCp.discount = newCp.calculateDiscountPercent();
            newCp.price = null;
        } else {
            newCp.price = newCp.calculateDiscountPrice();
            newCp.discount = null;
        }
        props.onChange(newCp);
    };

    const save = async () => {
        setSaving(true);
        await props.save(row);
        setSaving(false);
        props.cancel();
    };

    const cancel = () => {
        props.cancel();
    };

    const formatDiscountPercent = (percent) => {
        return percent < 0 ? `+${-1 * percent}%` : `-${percent}%`;
    };

    const isSaveDisabled = () => {
        if (saving) return true;
        if (row.discount === null && (row.price === null || row.price.length === 0)) return true;
        if (row.price === null && (row.discount === null || row.discount.length === 0)) return true;
        return false;
    };

    const getMarginClass = (marginPercentage) => {
        return marginPercentage > 0 ? styles.productMarginOk : styles.productMarginWarning;
    };

    const ContractPriceMargingDom = ({ prefix }) => {
        prefix = prefix ? prefix : '';
        return (
            <div>
                {row.margin() !== -1 && (
                    <span style={getMarginClass(row.marginPercentage())}>
                        {`${prefix}${formatCurrency(row.margin(), 2, props.currency)} (${row.marginPercentage()}%)`}
                    </span>
                )}
                {row.margin() === -1 && <span style={styles.productMarginWarning}>{`${prefix}*`}</span>}
            </div>
        );
    };

    const ContractPriceMargingDomRuleInUse = ({ prefix }) => {
        prefix = prefix ? prefix : '';
        const discountedPrice =
            row.product.priceWithoutVat - (row.product.priceWithoutVat * pricingRule.discount) / 100;
        const margin = discountedPrice - row.product.productionPrice;
        const marginPercentage = ((1 - row.product.productionPrice / discountedPrice) * 100).toFixed(2);

        return (
            <div>
                {margin !== -1 && (
                    <span style={getMarginClass(marginPercentage)}>
                        {`${prefix}${formatCurrency(margin, 2, props.currency)} (${marginPercentage}%)`}
                    </span>
                )}
                {margin === -1 && <span style={styles.productMarginWarning}>{`${prefix}*`}</span>}
            </div>
        );
    };

    const onViewLocations = async (e, contractNumberId, productNumber, bundleNumber) => {
        e.stopPropagation();
        await props.viewUsingLocations(contractNumberId, productNumber, bundleNumber);
    };

    return (
        <Grid name="product-row-container" item container sx={productRowSx} onClick={onSelectRow}>
            <Grid item xs={12} sm={4}>
                {row.product ? row.product.productName : row.productBundle.name}
                {row.productBundle && (
                    <span style={styles.bundleProductDetails}>
                        <br />
                        {row.productBundle.products.map((p) => (
                            <div key={p.product.id}>
                                {p.productCount} x {p.product.productName}
                            </div>
                        ))}
                    </span>
                )}
            </Grid>
            <Grid item xs={6} sm={2}>
                {formatCurrency(
                    row.product ? row.product.priceWithoutVat : row.productBundle.priceWithoutVat(),
                    2,
                    props.currency
                )}
            </Grid>
            <Grid item xs={12} sm={6}>
                {!edit && (
                    <Grid container>
                        <Grid item xs={8}>
                            <span>
                                {pricingRule ? (
                                    <Tooltip title={getPricingRuleTooltip()} arrow>
                                        <Grid item container>
                                            <Grid item xs={4}>
                                                {formatCurrency(
                                                    row.product.priceWithoutVat -
                                                        (row.product.priceWithoutVat * pricingRule.discount) / 100,
                                                    2,
                                                    props.currency
                                                )}
                                            </Grid>
                                            <Grid item xs={4}>
                                                {formatDiscountPercent(pricingRule.discount)}
                                            </Grid>
                                            <Grid item xs={4}>
                                                <ContractPriceMargingDomRuleInUse />
                                            </Grid>
                                        </Grid>
                                    </Tooltip>
                                ) : (
                                    <React.Fragment>
                                        {!row.price && !row.discount && <span>{t('product.noContractPrice')}</span>}
                                        {!!row.price && (
                                            <Grid item container>
                                                <Grid item xs={4}>
                                                    <strong>{formatCurrency(row.price, 3, props.currency)}</strong>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    {formatDiscountPercent(row.calculateDiscountPercent())}
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Box sx={styles.sectionDesktop}>
                                                        <ContractPriceMargingDom />
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        )}
                                        {!!row.discount && (
                                            <Grid item container>
                                                <Grid item xs={4}>
                                                    {formatCurrency(row.calculateDiscountPrice(), 3, props.currency)}
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <strong>{formatDiscountPercent(row.discount)}</strong>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Box sx={styles.sectionDesktop}>
                                                        <ContractPriceMargingDom />
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </React.Fragment>
                                )}
                            </span>
                        </Grid>
                        <Grid item xs={4}>
                            {row.inUse && !pricingRequest && <span>{t('product.inUse')}</span>}
                            {!row.inUse && !pricingRequest && <span>{t('product.notInUse')}</span>}
                            {!pricingRequest && (
                                <Button
                                    variant="contained"
                                    name="view-locations"
                                    size="small"
                                    color="primary"
                                    onClick={(e) =>
                                        onViewLocations(
                                            e,
                                            row.contractId,
                                            row.product ? row.product.storageId : null,
                                            row.productBundle ? row.productBundle.clusterId : null
                                        )
                                    }
                                    style={{ marginLeft: '0.5rem' }}>
                                    {t('general.locations')}
                                </Button>
                            )}
                        </Grid>
                        <Grid item xs={12} sx={styles.sectionMobile}>
                            <ContractPriceMargingDom prefix={t('general.margin') + ': '} />
                        </Grid>
                    </Grid>
                )}
                {edit && row.discount !== null && (
                    <Grid container>
                        <Grid item xs={12} md={5}>
                            <span>
                                {formatCurrency(row.calculateDiscountPrice(), 3, props.currency)}
                                /kpl
                            </span>
                        </Grid>
                        <Grid item xs={12} md={2} sx={styles.swapIconCol}>
                            <IconButton name="swap-price-discount" onClick={onDiscountPriceSwap} disabled={pricingRule}>
                                <SwapHorizIcon />
                            </IconButton>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <TextField
                                name="filterDiscount"
                                value={row.discount ? row.discount : ''}
                                onChange={handleDiscountChange}
                                onBlur={sanitizeDiscountValue}
                                disabled={pricingRule}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <ContractPriceMargingDom prefix={t('general.margin') + ': '} />
                        </Grid>
                    </Grid>
                )}
                {edit && row.discount === null && (
                    <Grid container>
                        <Grid item xs={12} md={5}>
                            <TextField
                                name="filterPrice"
                                value={row.price ? row.price : ''}
                                onChange={handlePriceChange}
                                onBlur={sanitizePriceValue}
                                disabled={pricingRule}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {currencySymbolFor(props.currency)}/{t('pcs')}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2} sx={styles.swapIconCol}>
                            <IconButton name="swap-price-discount" onClick={onDiscountPriceSwap} disabled={pricingRule}>
                                <SwapHorizIcon />
                            </IconButton>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <span>{row.calculateDiscountPercent()} %</span>
                        </Grid>
                        <Grid item xs={12}>
                            <ContractPriceMargingDom prefix={t('general.margin') + ': '} />
                        </Grid>
                    </Grid>
                )}
            </Grid>
            {highlight && !edit && !props.disabled && (
                <Grid item container>
                    <Grid item sx={styles.button}>
                        <Button
                            variant="contained"
                            name="edit-row"
                            color="primary"
                            onClick={editRow}
                            disabled={!!pricingRule}
                            startIcon={<EditIcon />}>
                            {t('buttons.edit')}
                        </Button>
                    </Grid>
                    {!pricingRequest && (
                        <Grid item sx={styles.button}>
                            <DeleteButton onSubmit={() => props.delete(row)} disabled={pricingRule} deleting={false} />
                        </Grid>
                    )}
                </Grid>
            )}
            {edit && (
                <Grid container spacing={1} sx={styles.withTopMargin}>
                    <Grid item>
                        <Button
                            name="save-row"
                            disabled={isSaveDisabled() || pricingRule}
                            variant="contained"
                            color="primary"
                            onClick={save}
                            startIcon={saving ? <CircularProgress color={'secondary'} size={'1rem'} /> : <SaveIcon />}>
                            {t('buttons.save')}
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            name="cancel"
                            disabled={saving || pricingRule}
                            variant="contained"
                            color="secondary"
                            onClick={cancel}
                            startIcon={<CancelIcon />}>
                            {t('buttons.cancel')}
                        </Button>
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
}

ContractPricesProduct.propTypes = {
    priceRow: PropTypes.object.isRequired,
};
